'use client';

import { useIntegrationManifests } from '@/app/settings/organization/integrations/getManifests';
import { usePerms } from '@/hooks/usePerms';
import { getLatestIntegrationService } from '@/lib/getManifestForIntegrationUserAgent';
import { useFetchAllInstances } from '@/requests/hooks/use-fetch-all-instances';
import type {
  IntegrationManifest,
  IntegrationUserAgent,
} from '@pigello/pigello-matrix';
import { useMemo } from 'react';

export type IntegrationUpgrade = {
  agent: IntegrationUserAgent;
  manifest: IntegrationManifest;
  serviceKey: string;
  servicePublicName: string;
  currentVersionLabel: string;
  latestVersionLabel: string;
  activationContentType?: string;
};

export type IntegrationUpgradeGroup = {
  manifest: IntegrationManifest;
  serviceKey: string;
  servicePublicName: string;
  latestVersionLabel: string;
  activationContentType?: string;
  upgrades: IntegrationUpgrade[];
};

type LatestServiceEntry = {
  manifest: IntegrationManifest;
  serviceKey: string;
  latestVersion: string;
  latestServicePublicName: string;
};

type LatestServiceLookup = Map<string, LatestServiceEntry>;

function buildLatestServiceLookup(
  manifests: IntegrationManifest[]
): LatestServiceLookup {
  const lookup: LatestServiceLookup = new Map();
  for (const manifest of manifests) {
    for (const [serviceKey, serviceVersions] of Object.entries(
      manifest.services
    )) {
      if (!serviceVersions) {
        continue;
      }
      const latest = getLatestIntegrationService(
        serviceVersions,
        manifest.uuid_conversions
      );
      if (!latest.version || !latest.service) {
        continue;
      }
      lookup.set(`${manifest.application_id}::${serviceKey}`, {
        manifest,
        serviceKey,
        latestVersion: latest.version,
        latestServicePublicName: latest.service.public_name,
      });
    }
  }
  return lookup;
}

function getUpgradeForAgent(
  agent: IntegrationUserAgent,
  lookup: LatestServiceLookup
): IntegrationUpgrade | null {
  const entry = lookup.get(`${agent.applicationId}::${agent.serviceId}`);
  if (!entry) {
    return null;
  }
  if (entry.latestVersion === agent.serviceVersionId) {
    return null;
  }
  const { manifest, serviceKey, latestVersion, latestServicePublicName } =
    entry;
  return {
    agent,
    manifest,
    serviceKey,
    servicePublicName: latestServicePublicName,
    currentVersionLabel:
      manifest.uuid_conversions[agent.serviceVersionId] ?? 'Okänd',
    latestVersionLabel: manifest.uuid_conversions[latestVersion] ?? 'Okänd',
    activationContentType: agent.activationContentType?.id,
  };
}

export function useIntegrationUpgrades() {
  const { canUpdate, canView, canCreate, canDelete } = usePerms(
    'integrationuseragent'
  );
  const canDoItAll = canCreate && canDelete && canUpdate && canView;

  const { data: activeIntegrations, isLoading: isLoadingAgents } =
    useFetchAllInstances<IntegrationUserAgent>({
      modelName: 'integrationuseragent',
      enabled: canDoItAll,
      filters: {
        is_active: { noop: true },
      },
    });

  const { manifests, isLoading: isLoadingManifests } = useIntegrationManifests({
    enabled: canDoItAll,
  });

  const latestServiceLookup = useMemo(() => {
    if (!manifests || !manifests.length) {
      return null;
    }
    return buildLatestServiceLookup(manifests);
  }, [manifests]);

  const upgrades = useMemo<IntegrationUpgrade[]>(() => {
    if (!canDoItAll) {
      return [];
    }
    if (!activeIntegrations?.list || !latestServiceLookup) {
      return [];
    }
    return activeIntegrations.list
      .map((agent) => getUpgradeForAgent(agent, latestServiceLookup))
      .filter((u): u is IntegrationUpgrade => u !== null);
  }, [canDoItAll, activeIntegrations?.list, latestServiceLookup]);

  const groups = useMemo<IntegrationUpgradeGroup[]>(() => {
    const byKey = new Map<string, IntegrationUpgradeGroup>();
    for (const upgrade of upgrades) {
      const key = `${upgrade.manifest.application_id}::${upgrade.serviceKey}`;
      const existing = byKey.get(key);
      if (existing) {
        existing.upgrades.push(upgrade);
        continue;
      }
      byKey.set(key, {
        manifest: upgrade.manifest,
        serviceKey: upgrade.serviceKey,
        servicePublicName: upgrade.servicePublicName,
        latestVersionLabel: upgrade.latestVersionLabel,
        activationContentType: upgrade.activationContentType,
        upgrades: [upgrade],
      });
    }
    return [...byKey.values()].sort((a, b) =>
      a.servicePublicName.localeCompare(b.servicePublicName, 'sv')
    );
  }, [upgrades]);

  return {
    upgrades,
    groups,
    isLoading: canUpdate && (isLoadingAgents || isLoadingManifests),
    canDoItAll,
  };
}
